/** A row as a listing lays it out: where it sits in the tree, and whether it holds one. */
export type Laid<Row> = {
  row: Row
  key: string
  depth: number
  branch: boolean
}

/**
 * The rows in the order they are drawn, each with the depth it sits at.
 *
 * A flat list is itself; a tree is walked from its roots, so a child is drawn
 * under its own parent whatever order the rows arrived in, and a branch somebody
 * shut takes everything beneath it with it.
 *
 * **Nothing a caller hands this is ever dropped**, and that is the whole reason
 * the walk starts from a computed set of roots rather than from trust. Two rows
 * naming each other as parent have no root between them, so a walk that took the
 * parent links at their word would visit neither and lose both — silently, in the
 * one place every listing in the app is about to depend on. A row inside a cycle
 * is shown at the TOP LEVEL instead: a listing that has lost a row is a worse
 * failure than one that shows it at the wrong depth, and the depth is the only
 * thing a cycle actually makes unanswerable.
 *
 * The tracker and the docs store both refuse a cycle on the way in, so this is
 * data neither of them can produce. It is written against the case anyway
 * because a guard is what makes that a fact about the writers rather than an
 * assumption this component is resting on.
 */
export function layOut<Row>(
  rows: Row[],
  rowKey: (row: Row) => string,
  parentOf: ((row: Row) => string | null) | undefined,
  shut: ReadonlySet<string>,
): Laid<Row>[] {
  if (!parentOf) {
    return rows.map((row) => ({
      row,
      key: rowKey(row),
      depth: 0,
      branch: false,
    }))
  }

  const byKey = new Map(rows.map((row) => [rowKey(row), row]))

  /**
   * Whether a row is its own ancestor.
   *
   * It asks about THIS row and not merely about a loop somewhere above it: a row
   * hanging below a cycle still has a real parent that will itself be drawn, so
   * it belongs under that parent and only the ring members come to the top.
   */
  const ownAncestor = (row: Row): boolean => {
    const start = rowKey(row)
    const seen = new Set<string>([start])
    let above = parentOf(row)
    while (above) {
      if (above === start) return true
      if (seen.has(above)) return false
      seen.add(above)
      const next = byKey.get(above)
      if (!next) return false
      above = parentOf(next)
    }
    return false
  }

  const roots: Row[] = []
  const under = new Map<string, Row[]>()
  for (const row of rows) {
    const parent = parentOf(row)
    /* A parent the list does not hold is no parent here: keeping the child under
       a row that is not there would drop it from the listing altogether. */
    if (parent && byKey.has(parent) && !ownAncestor(row)) {
      const kin = under.get(parent)
      if (kin) kin.push(row)
      else under.set(parent, [row])
    } else roots.push(row)
  }

  const out: Laid<Row>[] = []
  const walk = (list: Row[], depth: number) => {
    for (const row of list) {
      const key = rowKey(row)
      const kin = under.get(key)
      out.push({ row, key, depth, branch: Boolean(kin?.length) })
      if (kin?.length && !shut.has(key)) walk(kin, depth + 1)
    }
  }
  walk(roots, 0)
  return out
}
